import React, { useEffect, useRef, useState } from 'react';
import './herosection.css';
import { toAbsoluteUrl } from '../../assets/helpers/AssetHelpers';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import useReRouteHook from '../../assets/hooks/useReRouteHook';
import { getSearch } from '../../services/api/Api';
import { stopWords } from '../../utils/constant';

const HeroSection = () => {
	const [searchTerm, setSearchTerm] = useState('');
	const [suggestions, setSuggestions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [hasResults, setHasResults] = useState(true);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const navigate = useNavigate();
	const { reRouteButtonRenderer } = useReRouteHook();
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const closeDropdown = () => {
		setIsDropdownOpen(false);
		setSuggestions([]);
		setSearchTerm('');
	};

	// const fetchSuggestions = async (searchTerm: string) => {
	// 	try {
	// 		const response = await fetch(API_ENDPOINTS.getSearch, {
	// 			method: 'POST',
	// 			headers: new Headers({
	// 				'Content-Type': 'application/x-www-form-urlencoded',
	// 			}),
	// 			body: `search_str=${encodeURIComponent(searchTerm)}`,
	// 		});

	// 		const result = await response.json();
	// 		return result.data || [];
	// 	} catch (error) {
	// 		console.error('Error fetching search results:', error);
	// 		return [];
	// 	}
	// };

	const fetchSuggestions = async (searchTerm: string) => {
		try {
			const response = await getSearch(`search_str=${encodeURIComponent(searchTerm)}`);
			return response?.data || [];
		} catch (error) {
			console.log('Error fetching search result');
			return [];
		}
	};

	useEffect(() => {
		if (searchTerm) {
			setLoading(true);
			const timeoutId = setTimeout(async () => {
				const fetchedResults = await fetchSuggestions(searchTerm);

				setSuggestions(fetchedResults);
				setLoading(false);

				setHasResults(fetchedResults.length > 0);
			}, 500);

			return () => clearTimeout(timeoutId);
		} else {
			setSuggestions([]);
			setLoading(false);
		}
	}, [searchTerm]);

	const handleKeyPress = (e: any) => {
		if (loading) {
			return;
		}

		if (e.key === 'Enter') {
			if (suggestions.length > 0 && hasResults) {
				navigate(`/search-results/${searchTerm}`);
			} else {
				navigate(`/no-results-found/${searchTerm}`);
			}
			setSuggestions([]);
			setSearchTerm('');
			setIsDropdownOpen(false);
		}
	};

	const handleSearchTerm = (term: any) => {
		setSearchTerm(term);
		setIsDropdownOpen(!!term);
	};

	const updatedSuggestions = suggestions.map((item: any) => {
		const uniqueParentMapSet = new Set(
			item?.parent_map.map((suggestionItem: any) => suggestionItem),
		);
		return {
			...item,
			parent_map: Array.from(uniqueParentMapSet),
		};
	});

	return (
		<>
			<section className="" style={{ backgroundColor: '#E6F6DE' }}>
				<div className="container">
					<div className="row">
						<div className="col text-center">
							<div className="row">
								<div className="col-lg-11 mx-auto px-4 px-md-5 px-lg-2 hero-section-container">
									<div className="dropdown" style={{ position: 'relative' }} ref={dropdownRef}>
										<div className="ur_ebold color30 search-section-title text-start mb-4">
											Hello! How Can we help you today?
										</div>
										<div className="btn-group w-100 h-48">
											<div
												style={{
													position: 'absolute',
													left: '15px',
													top: '50%',
													transform: 'translateY(-50%)',
													zIndex: 2,
												}}>
												<img
													src={toAbsoluteUrl('herosection-images/searchicon.svg')}
													className="w-100 h-100"
													alt="Search Icon"
												/>
											</div>
											<input
												className="btn btn-lg in_med border-secondary fs-6 bg-light text-start ps-5 py-3 px-2"
												style={{ fontSize: '16px', borderRadius: '8px' }}
												type="text"
												placeholder="To help you, type your query here."
												value={searchTerm}
												onChange={(e) => {
													handleSearchTerm(e.target.value);
												}}
												onFocus={() => {
													if (searchTerm) {
														setIsDropdownOpen(true);
													}
												}}
												onKeyPress={(e) => {
													if (e.key === 'Enter') {
														if (!searchTerm.trim()) {
															e.preventDefault();
														} else {
															handleKeyPress(e);
														}
													}
												}}
												aria-label="Search"
											/>
											{searchTerm && (
												<button
													onClick={(e) => handleSearchTerm('')}
													style={{
														position: 'absolute',
														right: '10px',
														top: '56%',
														transform: 'translateY(-50%)',
														background: 'none',
														border: 'none',
														cursor: 'pointer',
														zIndex: 1,
													}}>
													<FontAwesomeIcon
														className="p-1"
														icon={faCircleXmark}
														style={{ color: 'gray' }}
													/>
												</button>
											)}

											<ul
												style={{ maxHeight: '500px' }}
												className={`dropdown-menu mt-1 custom-scroll ${
													searchTerm && isDropdownOpen ? 'show w-100' : ''
												}`}>
												{loading ? (
													<li>
														<div className="search-result-message px-4 py-1 bw_med text-center">
															Searching Results
														</div>
													</li>
												) : searchTerm && updatedSuggestions.length > 0 ? (
													updatedSuggestions.map((item: any) => {
														const escapeRegex = (str: string) =>
															str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

														const searchWords = searchTerm
															.toLowerCase()
															.split(/\s+/)
															.filter(Boolean)
															.map(escapeRegex)
															.filter((word) => !stopWords.has(word.toLowerCase()));

														const wordsToHighlight =
															searchWords.length > 0
																? searchWords
																: searchTerm
																		.toLowerCase()
																		.split(/\s+/)
																		.filter(Boolean)
																		.map(escapeRegex);

														const highlightedTitle = item.title
															.split(new RegExp(`\\b(${wordsToHighlight.join('|')})\\b`, 'gi'))
															.map((part: any, index: number) => {
																if (
																	part &&
																	wordsToHighlight.some(
																		(word) => part.toLowerCase() === word.toLowerCase(),
																	)
																) {
																	return <strong key={index}>{part}</strong>;
																}
																return part;
															});

														const cmsCategory = item.cms_category_id
															? item.cms_category_id.toLowerCase().replace(/\s+/g, '-')
															: null;

														const parentCategory = item.parent_map_id?.[0]
															? item.parent_map_id[0].toLowerCase().replace(/\s+/g, '-')
															: null;

														const MainCategory = item.parent_map_id?.[1]
															? item.parent_map_id[1].toLowerCase().replace(/\s+/g, '-')
															: null;

														if (!cmsCategory || !parentCategory || !MainCategory) {
															console.warn('Some categories are missing or undefined', {
																cmsCategory,
																parentCategory,
																MainCategory,
															});
														}

														const highlightedCategory = item.cms_category
															.split(new RegExp(`\\b(${wordsToHighlight.join('|')})\\b`, 'gi'))
															.map((part: any, index: number) =>
																wordsToHighlight.some(
																	(word) => part.toLowerCase() === word.toLowerCase(),
																) ? (
																	<span key={index} className="highlightedCategoryColor">
																		{part}
																	</span>
																) : (
																	part
																),
															);

														return (
															<li key={item.title} className="py-3 px-4 in_reg border-bottom ms-2">
																<div className="fs-12 mb-2 color34">
																	{reRouteButtonRenderer({
																		url:
																			cmsCategory && parentCategory && MainCategory
																				? `/category/${encodeURIComponent(
																						MainCategory,
																					)}/${encodeURIComponent(parentCategory)}`
																				: '#',
																		classes: 'suggestion-item fs-6 p-0 w-100',
																		nameText: highlightedCategory,
																		onClick: closeDropdown,
																	})}
																</div>
																<div>
																	{reRouteButtonRenderer({
																		url:
																			cmsCategory && parentCategory && MainCategory
																				? `/category/${encodeURIComponent(
																						MainCategory,
																					)}/${encodeURIComponent(
																						parentCategory,
																					)}/${encodeURIComponent(
																						cmsCategory,
																					)}/${encodeURIComponent(item.id)}`
																				: '#',
																		classes: 'suggestion-item fs-6 p-0 w-100',
																		nameText: highlightedTitle,
																		onClick: closeDropdown,
																	})}
																</div>
															</li>
														);
													})
												) : (
													<li>
														<div
															className="no-results-message px-4 py-1 in_reg"
															style={{ borderRadius: '10px' }}>
															{searchTerm === '' ? 'Write anything to search' : 'No Result Found'}
														</div>
													</li>
												)}
											</ul>
										</div>
									</div>
									<div className="pt-4 text-decortion-none d-none">
										<ul className="d-flex justify-content-evenly list-unstyled">
											<li>
												<a href="" className="link-color1 text-decoration-underline">
													Bulletin
												</a>
												<sup className="px-1 text-decoration-none" style={{ color: '#2B948F' }}>
													New
												</sup>
											</li>
											<li>
												<a href="" className="link-color1 text-decoration-underline">
													Circulars
												</a>
											</li>
											<li>
												<a href="" className="link-color1 text-decoration-underline">
													Announcement
												</a>
											</li>
										</ul>
									</div>
									{/* <MostSearchFaq /> */}
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		</>
	);
};

export default HeroSection;
